/**
 * Click and hover handlers for static (image-bounds) site plan overlays.
 *
 * Injected into sitePlan.plugin via the onSitePlanClick, onSitePlanHover, and
 * onSitePlanLeave options so the plugin stays free of store, DOM event, and
 * navigation dependencies.
 *
 * Click behaviour:
 *   Desktop – dispatches 'communityMapCLicked' analytics event and navigates.
 *   Mobile  – first tap dispatches 'sitePlanTapped' custom event (opens the
 *             carousel); second tap on the same community navigates.
 *
 * Hover behaviour:
 *   Applies/removes the active CSS class and dispatches 'sitePlanHovered' /
 *   'sitePlanHoverEnded' custom events so sitePlanMiniCard.plugin can react
 *   without coupling to this handler.
 *
 * @module searchResultsMap/handlers/staticSitePlanHandlers
 */

import MarkerType from '../enums/markerType';
import { selectMapResults } from '../redux/selectors';
import { sitePlanTapped$, sitePlanHovered$, sitePlanHoverEnded$ } from '../redux/observables';
import { SITE_PLAN_CLASS_NAMES } from '../services/sitePlanBehavior';

/**
 * Creates the static site plan click handler.
 *
 * @param {Function} getState    - Returns the current Redux store state.
 * @param {Function} isDesktopFn - Returns true when the viewport is desktop.
 * @returns {Function} Handler called with the full sitePlan object on overlay click.
 */
export function createStaticSitePlanClickHandler(getState, isDesktopFn) {
  let lastTappedCommunityId = null;

  return function handleStaticSitePlanClick(sitePlan) {
    const results = selectMapResults(getState()) ?? [];
    const preferredTypes = [MarkerType.HousingDevelopment.toString(), MarkerType.BuildablePlan.toString()];
    const preferredMatch = results.find(({ communityId: resultCommunityId, type }) =>
      resultCommunityId === sitePlan.communityId && preferredTypes.includes(type)
    );
    const resultMatch = preferredMatch ?? results.find(({ communityId: resultCommunityId }) =>
      resultCommunityId === sitePlan.communityId
    );
    const isPreferredMatch = Boolean(preferredMatch);
    
    const url = preferredMatch?.url ?? `/communitydetail/community-${resultMatch?.communityId}`;

    document.body.dispatchEvent(
      new CustomEvent('communityMapClicked', {
        bubbles: true,
        detail: {
          communityMap: {
            id: resultMatch?.communityId,
            name: resultMatch?.communityName ?? resultMatch?.name,
            isInteractive: false,
          },
        },
      })
    );

    if (isDesktopFn?.() || !isPreferredMatch) {
      window.open(url, '_blank');
      return;
    }

    // Mobile: second tap on the same community navigates.
    if (lastTappedCommunityId === sitePlan.communityId) {
      window.open(url, '_blank');
      lastTappedCommunityId = null;
      return;
    }

    lastTappedCommunityId = sitePlan.communityId;
    sitePlanTapped$.next({ communityId: sitePlan.communityId });
  };
}

/**
 * Creates hover handlers for static site plan overlays.
 * Applies/removes the active CSS class on ALL site plan containers that share
 * the same communityId, so multi-plan communities highlight as a group.
 *
 * Dispatches 'sitePlanHovered' and 'sitePlanHoverEnded' custom events so
 * sitePlanMiniCard.plugin can react without coupling to this handler.
 *
 * Requires that the overlay container element carries a
 * `data-community-id` attribute (set by overlays.js).
 *
 * @returns {{ onSitePlanHover: Function, onSitePlanLeave: Function }}
 */
export function createStaticSitePlanHoverHandler() {
  function getCommunityWrapper(communityId) {
    return document.querySelector(`[data-static-site-plan-community-id="${communityId}"]`);
  }

  return {
    onSitePlanHover(sitePlan) {
      getCommunityWrapper(sitePlan.communityId)?.classList.add(SITE_PLAN_CLASS_NAMES.active);
      sitePlanHovered$.next({ communityId: sitePlan.communityId });
    },
    onSitePlanLeave(sitePlan) {
      getCommunityWrapper(sitePlan.communityId)?.classList.remove(SITE_PLAN_CLASS_NAMES.active);
      sitePlanHoverEnded$.next({ communityId: sitePlan.communityId });
    },
  };
}
